import { useAtomValue } from 'jotai';
import { UserCircle2, ChevronDown, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePageTitle } from '@/hooks/pages';
import { useAuthData, useExpireAuthData } from '@/hooks/auth';
import { globalStatusAtom } from '@/hooks/status';
import { FxMonitorHealth } from '@shared/enums';
import { playerlistAtom } from '@/hooks/playerlist';
import { useLocation } from 'wouter';

export function ScHeader() {
  const [, setLocation] = useLocation();
  const pageTitle = usePageTitle();
  const authData = useAuthData();
  const expireSession = useExpireAuthData();
  const globalStatus = useAtomValue(globalStatusAtom);
  const playerlist = useAtomValue(playerlistAtom);

  const serverName = globalStatus?.server?.name ?? window.txConsts.server?.name ?? 'SC Labs';
  const health = globalStatus?.server?.health;
  const isOnline = health === FxMonitorHealth.ONLINE;
  const isPartial = health === FxMonitorHealth.PARTIAL;
  const playerCount = playerlist?.length ?? 0;

  const statusColor = isOnline ? 'var(--sc-green)' : isPartial ? 'var(--sc-warn)' : '#ef4444';
  const statusLabel = isOnline ? 'Online' : isPartial ? 'Partial' : 'Offline';

  return (
    <header
      className="sticky top-0 z-20 flex items-center h-[48px] shrink-0 px-5 gap-4"
      style={{
        background: 'rgba(7,9,16,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* Page title */}
      <h1 className="text-sm font-bold text-white tracking-tight flex-1 min-w-0 truncate">
        {pageTitle}
      </h1>

      {/* Server status pill */}
      <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <span className="truncate max-w-[140px] font-mono text-[#94a3b8]">{serverName}</span>
        <span className="font-bold" style={{ color: statusColor }}>{statusLabel}</span>
      </div>

      {/* Player count */}
      {isOnline && (
        <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold font-mono"
          style={{ background: 'rgba(0,255,209,0.06)', border: '1px solid rgba(0,255,209,0.12)', color: '#00ffd1' }}>
          {playerCount} players
        </div>
      )}

      {/* Account dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-2.5 py-1 rounded-xl transition-all duration-150
                             border border-transparent hover:bg-white/[0.05] hover:border-white/[0.08]"
            aria-label="Account menu">
            <UserCircle2 size={16} className="text-[#94a3b8]" />
            <span className="hidden sm:block text-[11px] font-semibold text-[#94a3b8] max-w-[80px] truncate">
              {authData?.name ?? 'Account'}
            </span>
            <ChevronDown size={12} className="text-[#475569]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={() => setLocation('/settings')}>
            <Settings size={13} className="mr-2" /> Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
            onClick={() => expireSession('header logout', 'user clicked logout')}
          >
            <LogOut size={13} className="mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
